import { Component, EventEmitter, Output, effect, inject, signal, untracked, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TrackingApiService } from '../services/tracking-api.service';
import { TrackingStoreService } from '../services/tracking-store.service';
import { durationSecToStr, launchSourceLabel, parseApiDate, shortId, statusClass, statusLabel } from '../tracking-format.util';
import type { HistoryQuery, TrackingSession } from '../tracking-demo.models';

/** History tab: completed/terminal sessions with server-side filters + pagination.
 *  Every demo is ONE merged row (host + guests + outcome); a row opens the full report. */
@Component({
  selector: 'app-session-history',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './session-history.component.html',
})
export class SessionHistoryComponent {
  private readonly api = inject(TrackingApiService);
  private readonly store = inject(TrackingStoreService);

  /** The admin asked for a session's full report. */
  @Output() reportRequested = new EventEmitter<TrackingSession>();

  readonly rows = signal<TrackingSession[]>([]);
  readonly total = signal(0);
  readonly page = signal(1);
  readonly pageSize = 25;
  readonly loading = signal(false);

  readonly filters: HistoryQuery = {};
  /** Started-date bounds ('YYYY-MM-DD' from the native pickers); local-day boundaries are
   *  converted to UTC instants in `load()` so a day means the ADMIN's day, not the server's. */
  dateFrom = '';
  dateTo = '';
  readonly statusOptions = ['', 'ended', 'disconnected_by_admin', 'expired', 'blocked', 'rejected'];
  /** "Source" filter values — the backend's `appPlatform` history filter vocabulary. */
  readonly sourceOptions = [
    { value: '', label: 'All sources' },
    { value: 'app', label: 'Mobile app (any)' },
    { value: 'ios', label: 'App · iOS' },
    { value: 'android', label: 'App · Android' },
    { value: 'web', label: 'Web' },
  ];

  readonly durationSecToStr = durationSecToStr;
  readonly launchSourceLabel = launchSourceLabel;
  readonly shortId = shortId;

  /** UA-sniffed device type for the Source cell's second line ('' when unknown). */
  deviceOf(s: TrackingSession): string {
    const d = s.security?.deviceType;
    return d && d !== 'unknown' ? d : '';
  }
  readonly statusClass = statusClass;
  readonly statusLabel = statusLabel;

  private debounce: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Initial load + reload whenever the header's Dummy/Live toggle flips. `untracked`
    // keeps page()/other signals read inside load() from becoming effect dependencies.
    effect(() => {
      this.store.dataType();
      untracked(() => { this.page.set(1); void this.load(); });
    });
  }

  onFilterChange(): void {
    if (this.debounce) clearTimeout(this.debounce);
    this.debounce = setTimeout(() => { this.page.set(1); this.load(); }, 400);
  }

  totalPages(): number { return Math.max(1, Math.ceil(this.total() / this.pageSize)); }

  prev(): void { if (this.page() > 1) { this.page.update((p) => p - 1); this.load(); } }
  next(): void { if (this.page() < this.totalPages()) { this.page.update((p) => p + 1); this.load(); } }

  async load(): Promise<void> {
    this.loading.set(true);
    try {
      const q: HistoryQuery = {
        ...this.filters,
        page: this.page(),
        pageSize: this.pageSize,
        dataType: this.store.dataType(),
      };
      // Local-day boundaries → UTC instants (created_at is naive-UTC server-side).
      if (this.dateFrom) q.from = new Date(`${this.dateFrom}T00:00:00`).toISOString();
      if (this.dateTo) q.to = new Date(`${this.dateTo}T23:59:59.999`).toISOString();
      const res = await this.api.getHistory(q);
      this.rows.set(res.items || []);
      this.total.set(res.total || 0);
    } catch {
      /* handled by api */
    } finally {
      this.loading.set(false);
    }
  }

  /** Human label for how the demo ended. */
  outcomeLabel(s: TrackingSession): string {
    const map: Record<string, string> = {
      summary: 'Completed · summary saved',
      host_ended: 'Host ended the demo',
      admin_disconnect: 'Terminated by admin',
      sweeper_idle: 'Idle timeout',
      expired: 'Expired',
    };
    const base = map[s.endReason || ''] || (s.endReason || '—');
    return s.adminDisconnectReason ? `${base} (${s.adminDisconnectReason.replace(/_/g, ' ')})` : base;
  }

  /** "Jul 7, 2026" | "09:31" split for the Started column. */
  startedDate(s: TrackingSession): string {
    const d = parseApiDate(s.startedAt || s.createdAt);
    return d ? d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  }

  startedTime(s: TrackingSession): string {
    const d = parseApiDate(s.startedAt || s.createdAt);
    return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
  }

  locationOf(s: TrackingSession): string {
    const sec = s.security;
    if (!sec) return '—';
    return [sec.city, sec.country].filter(Boolean).join(', ') || '—';
  }
}
